import { useEffect, useState } from "react";
import axiosInstant from "../../api";
import {
    dashboardGeneralEndpoint,
    dashboardPipelineEndpoint,
    dashboardExpertsEndpoint,
    dashboardFulfillmentEndpoint,
    dashboardShootLocationsEndpoint,
    dashboardSalesEndpoint,
    dashboardProductCategoriesEndpoint,
    dashboardProductSubCategoriesEndpoint,
    dashboardProductSubSubCategoriesEndpoint,
    dashboardSubProfessionsEndpoint,
    dashboardAreasOfFocusEndpoint,
    getProfessionData,
    getStateData,
} from "../../api/endpoints";

interface Card {
    val: number;
    change?: number;
    pct?: number;
    target?: number | null;
}

export interface GeneralData {
    brand_accounts: Card;
    new_brands: Card;
    total_products: Card;
    total_orders: Card;
    new_orders: Card & { unassigned: number };
    active_projects: Card;
    completed: Card;
}

export interface PipelineRow {
    key: string;
    label: string;
    val: number;
    barPct: number;
    alert: string | null;
}

export interface ExpertsData {
    total_experts: Card;
    new_this_period: Card;
    awaiting_proposals: Card & { urgent: number };
    waiting_on_confirmation: { val: number; target: number };
    total_experience_years: { val: number };
}

// Deliverables split by the Videos page's project / non-project views.
interface ProjectSplit {
    project?: number;
    non_project?: number;
}

export interface FulfillmentData {
    total_deliverables: Card & ProjectSplit;
    waiting_estimated_time: { val: number; target: number };
    projects_needs_scheduling: { val: number; target: number };
    projects_scheduled: { val: number; target: number; alert: string | null };
    projects_filming: { val: number; target: number; alert: string | null };
    projects_editing: { val: number; target: number };
    projects_in_review: { val: number; target: number };
    waiting_on_approval: { val: number; target: number; overdue: number } & ProjectSplit;
    approved_deliverables: Card & ProjectSplit;
    internal_statuses?: { label: string; val: number }[];
}

export interface Segment {
    label: string;
    val: number;
    color: string;
}

export interface GeoData {
    total: number;
    segments: Segment[];
}

export interface SalesData {
    revenue: { val: number; change_pct: number; spark: number[]; stub?: boolean };
    content_types: { val: number };
    channels: { val: number };
    platforms: { val: number };
    campaigns_created: Card;
    leads: Card;
    pdp_audits: Card;
    proposals: Card;
    // Proposals stamped with a PDP channel sub-type (period-scoped).
    pdp_types?: { retail: number; d2c: number; amazon: number };
}

export interface PeriodArg {
    period: string;
    from?: string;
    to?: string;
}

function useFetch<T>(endpoint: string, arg: PeriodArg) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);
    const { period, from, to } = arg;
    const isCustomReady = period === "custom" && from && to;
    const skip = period === "custom" && !isCustomReady;

    useEffect(() => {
        if (skip) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const params: Record<string, string> = { period };
        if (isCustomReady) {
            params.from = from!;
            params.to = to!;
        }
        axiosInstant
            .get<T>(endpoint, { params })
            .then((res) => {
                if (!cancelled) setData(res.data);
            })
            .catch((err) => {
                if (!cancelled) setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [endpoint, period, from, to]);

    return { data, loading, error };
}

export const useGeneral = (arg: PeriodArg) => useFetch<GeneralData>(dashboardGeneralEndpoint, arg);
export const usePipeline = (arg: PeriodArg) => useFetch<{ rows: PipelineRow[] }>(dashboardPipelineEndpoint, arg);
export const useExperts = (arg: PeriodArg) => useFetch<ExpertsData>(dashboardExpertsEndpoint, arg);
export const useFulfillment = (arg: PeriodArg) => useFetch<FulfillmentData>(dashboardFulfillmentEndpoint, arg);
export const useShootLocations = (arg: PeriodArg) => useFetch<GeoData>(dashboardShootLocationsEndpoint, arg);
export const useSales = (arg: PeriodArg) => useFetch<SalesData>(dashboardSalesEndpoint, arg);

export const useProfessions = (arg: PeriodArg) =>
    useFetch<Array<{ name: string; total: number }>>(getProfessionData, arg);
export const useExpertGeo = (arg: PeriodArg) =>
    useFetch<Array<{ state: string; total: number }>>(getStateData, arg);
export const useProductCategories = (arg: PeriodArg) =>
    useFetch<Array<{ name: string; total: number }>>(dashboardProductCategoriesEndpoint, arg);
export const useProductSubCategories = (arg: PeriodArg) =>
    useFetch<Array<{ name: string; total: number }>>(dashboardProductSubCategoriesEndpoint, arg);
export const useProductSubSubCategories = (arg: PeriodArg) =>
    useFetch<Array<{ name: string; total: number }>>(dashboardProductSubSubCategoriesEndpoint, arg);
export const useSubProfessions = (arg: PeriodArg) =>
    useFetch<Array<{ name: string; total: number }>>(dashboardSubProfessionsEndpoint, arg);
export const useAreasOfFocus = (arg: PeriodArg) =>
    useFetch<Array<{ name: string; total: number }>>(dashboardAreasOfFocusEndpoint, arg);
